import { Box, Drawer, Stack } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useEffect } from "react";
import usePermissionDialog from "src/components/permissions/hook/usePermissionDialog";
import PermissionModal from "src/components/permissions/permissionModal";
import { useBanner } from "src/contexts/BannerContext";
import menuBar from "src/layouts/dashboard/navbar/NavConfig";
import { RootState, useSelector } from "@redux/store";
import { CustomTheme } from "src/@types/customTheme";
import Logo from "../../../components/Logo";
import { NavSectionVertical } from "../../../components/nav-section";
import Scrollbar from "../../../components/Scrollbar";
import { NAVBAR } from "../../../config";
import useCollapseDrawer from "../../../hooks/useCollapseDrawer";
import useResponsive from "../../../hooks/useResponsive";
import cssStyles from "../../../utils/cssStyles";
import CollapseButton from "./CollapseButton";
import NavbarAccount from "./NavbarAccount";
const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    transition: theme.transitions.create("width", {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

type Props = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
};

export default function NavbarVertical({
  isOpenSidebar,
  onCloseSidebar,
}: Props) {
  const theme = useTheme();
  const { visible } = useBanner();
  const isDesktop = useResponsive("up", "lg");
  const { isCollapse, collapseClick, collapseHover, onToggleCollapse } =
    useCollapseDrawer();
  const customTheme = useSelector(
    (state: RootState) => state.accountInfo.accountInfo?.customTheme,
  );
  const {
    handleCloseModuleDialog,
    handleOpenModuleDialog,
    moduleName,
    openModuleDialog,
  } = usePermissionDialog();
  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Scrollbar
        sx={{
          flexGrow: 1,
          height: 1,
          "& .simplebar-content": {
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Stack
          spacing={0.5}
          sx={{
            gap: 2,
            flexShrink: 0,
            padding: 3,
            pb: 4,
            overflowX: "hidden",
            ...(isCollapse && { alignItems: "center" }),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-end" }}>
            <Logo isCollapse={isCollapse} />
          </Box>
          <NavSectionVertical
            navConfig={menuBar().navConfig}
            isCollapse={isCollapse}
            handleOpenModuleDialog={handleOpenModuleDialog}
          />
          <PermissionModal
            open={openModuleDialog}
            handleClose={handleCloseModuleDialog}
            moduleName={moduleName}
          />
        </Stack>
      </Scrollbar>

      <Box
        sx={{
          flexShrink: 0,
          p: 3,
          pt: 1,
          bgcolor: "background.default",
        }}
      >
        <NavbarAccount isCollapse={isCollapse} />
      </Box>

      {isDesktop && (
        <Box
          sx={{
            position: "absolute",
            top: 20,
            right: -20,
            zIndex: 9999,
          }}
        >
          <CollapseButton
            theme={theme}
            onToggleCollapse={onToggleCollapse}
            collapseClick={collapseClick}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse
            ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH
            : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: "absolute",
        }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              top: visible ? 40 : 0,
              height: visible ? "calc(100% - 40px)" : "100%",
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              overflow: "visible",
              borderRight: `1px solid ${theme.palette.text.borderLine}`,
              ...(customTheme === CustomTheme.Sage && {
                borderRight: `4px solid ${theme.palette.sage_green}`,
              }),
              width: NAVBAR.DASHBOARD_WIDTH,
              bgcolor: "background.default",
              transition: (theme) =>
                theme.transitions.create("width", {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
              // 🔥 This is what actually moves the sidebar down
              position: "fixed",
              top: visible ? 40 : 0, // same height as banner
              height: visible ? "calc(100% - 40px)" : "100%", // adjust height so it doesn't overflow
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
